// Module: markerLabelLayout.js
// Purpose: ONE pure layout authority for chart marker label text (50-13, Option B / D23). Rewrites only
//          each marker's `text`: the head of a colliding cluster keeps its text + ' +N', the rest get ''.
//          Glyphs stay on lightweight-charts createSeriesMarkers; hidden text is listed per bar for hover.

import { MARKER_LABEL_MIN_CANVAS_PX } from './utils';

//--> Constants
//* Library price-line title box at font 12: height = fontSize + 2 x 2.5 padding = 17 (lightweight-charts.development.mjs:304/:865),
//* drawn right-aligned at the pane's right edge (PanePriceAxisViewRenderer :1786-1803, alignRight xInside :879-893).
export const LEVEL_LABEL_HALF_HEIGHT_PX = 17 / 2;
//* Width padding around the title text: borderSize 1 + paddingInner 5 + paddingOuter 5 (:281-282, :866-868).
export const LEVEL_TITLE_PAD_PX = 11;
const GLYPH_WIDTH_PX = 12;
const JSDOM_CHAR_PX = 6.5;
const KIND_RANK = { trade: 1, signal: 2, angle: 3 };

//* Test counter hook (R6-T2): number of layout runs.
export const markerLabelLayoutStats = { calls: 0 };

//--> Text measurement: one cached CanvasRenderingContext2D.measureText, chars x 6.5px fallback (jsdom).
export const makeTextMeasurer = (ctx, font) => {
    const cache = new Map();
    let fontSet = false;
    return (text) => {
        const s = String(text ?? '');
        if (cache.has(s)) return cache.get(s);
        let w = s.length * JSDOM_CHAR_PX;
        try {
            if (ctx && typeof ctx.measureText === 'function') {
                if (!fontSet && font) { ctx.font = font; fontSet = true; }
                const m = ctx.measureText(s);
                if (m && Number.isFinite(m.width) && m.width > 0) w = m.width;
            }
        } catch (e) { /* keep fallback width */ }
        cache.set(s, w);
        return w;
    };
};

//--> Precedence: entry/exit > signal > angle. A trade marker whose text starts 'SIGNAL' is a signal.
const rankOf = (m) => {
    if (m.kind && KIND_RANK[m.kind]) return KIND_RANK[m.kind];
    return String(m.text || '').startsWith('SIGNAL') ? KIND_RANK.signal : KIND_RANK.trade;
};
const timeKey = (t) => (typeof t === 'string' ? new Date(t).getTime() : Number(t));
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
//* Deterministic tie-break: precedence, earlier time, then marker id (+ stable field fallbacks).
const byPriority = (a, b) =>
    (a.rank - b.rank) || (a.t - b.t) || cmp(a.id, b.id) || cmp(a.m.text || '', b.m.text || '');

const extent = (items, widthOf) => {
    let lo = Infinity; let hi = -Infinity;
    for (const it of items) {
        const w = widthOf(it);
        lo = Math.min(lo, it.x - w / 2); hi = Math.max(hi, it.x + w / 2);
    }
    return [lo, hi];
};

/**
 * @param {Array} markers  { time, position, text, id?, kind?, logical?, anchorY? }
 * @param {{timeToX:Function, barSpacing?:number, paneHeight?:number, levelRows?:{y:number,titleWidth:number}[], paneWidth?:number, measureText?:Function}} opts
 * @returns {{markers:Array, hoverByIndex:Object}} same-length, same-order copy with only `text` rewritten
 */
export const layoutMarkerLabels = (markers, opts = {}) => {
    markerLabelLayoutStats.calls += 1;
    const { timeToX, paneHeight, paneWidth, levelRows, measureText } = opts;
    const measure = measureText || ((t) => String(t).length * JSDOM_CHAR_PX);
    const small = Number.isFinite(paneHeight) && paneHeight < MARKER_LABEL_MIN_CANVAS_PX;
    const rows = Array.isArray(levelRows) ? levelRows.filter(r => r && Number.isFinite(r.y) && r.titleWidth > 0) : [];

    const items = markers.map((m, i) => ({
        m, i, full: m.text || '', text: m.text || '', rank: rankOf(m), t: timeKey(m.time),
        id: String(m.id ?? ''), x: typeof timeToX === 'function' ? timeToX(m.time) : null,
        head: false, members: null, side: m.position || 'aboveBar', w: undefined,
    }));
    const widthOf = (it) => (it.w === undefined ? (it.w = measure(it.full) + GLYPH_WIDTH_PX) : it.w);

    //--> 1. Level rows win (D16): a text whose drawn box touches a level's right-edge title box drops to hover only.
    //*    Title-less (axis-label-only) rows never reach here; they live on the price scale outside the pane.
    const levelDropped = new Set();
    if (!small && rows.length && Number.isFinite(paneWidth)) {
        for (const it of items) {
            if (!it.full || !Number.isFinite(it.m.anchorY) || !Number.isFinite(it.x)) continue;
            const lo = it.x - widthOf(it) / 2; const hi = it.x + widthOf(it) / 2;
            const hit = rows.some(r => Math.abs(r.y - it.m.anchorY) < 2 * LEVEL_LABEL_HALF_HEIGHT_PX
                && lo < paneWidth && hi > paneWidth - r.titleWidth);
            if (hit) { levelDropped.add(it); it.text = ''; }
        }
    }

    //--> 2. Cluster per side: x-intervals that intersect, head box includes its ' +N' / 'N events' suffix.
    const candidates = items.filter(it => it.full && Number.isFinite(it.x) && !levelDropped.has(it));
    const sides = new Map();
    for (const it of candidates) { if (!sides.has(it.side)) sides.set(it.side, []); sides.get(it.side).push(it); }
    const suffixOf = (n) => (small ? `${n} events` : (n > 1 ? ` +${n - 1}` : ''));
    //* Incremental cluster (O(1) merge): running extent, head and head box; the member array is reused.
    const boxFor = (head, count) => {
        const w = small ? measure(suffixOf(count)) + GLYPH_WIDTH_PX : widthOf(head) + (count > 1 ? measure(suffixOf(count)) : 0);
        return [head.x - w / 2, head.x + w / 2];
    };
    const mk = (it) => ({ members: [it], head: it, ext: extent([it], widthOf), hb: boxFor(it, 1) });
    const merge = (a, b) => {
        for (const it of b.members) a.members.push(it);
        a.head = byPriority(a.head, b.head) <= 0 ? a.head : b.head;
        a.ext = [Math.min(a.ext[0], b.ext[0]), Math.max(a.ext[1], b.ext[1])];
        a.hb = boxFor(a.head, a.members.length);
        return a;
    };
    const overlaps = (a, b) => a[0] < b[1] && b[0] < a[1];

    const finalClusters = [];
    for (const list of sides.values()) {
        list.sort((a, b) => (a.x - b.x) || byPriority(a, b));
        const stack = [];
        for (const it of list) {
            stack.push(mk(it));
            while (stack.length > 1) {
                const top = stack[stack.length - 1]; const prev = stack[stack.length - 2];
                if (!(overlaps(prev.ext, top.ext) || overlaps(prev.hb, top.hb) || overlaps(prev.hb, top.ext) || overlaps(prev.ext, top.hb))) break;
                stack.pop(); stack.pop();
                stack.push(merge(prev, top));
            }
        }
        finalClusters.push(...stack);
    }

    //--> 3. Rewrite text: head keeps text + suffix, members ''. Small mode: glyph-only, head = 'N events'.
    for (const c of finalClusters) {
        const h = c.head;
        h.head = true; h.members = c.members;
        for (const it of c.members) it.text = '';
        if (!small) h.text = h.full + suffixOf(c.members.length);
    }
    if (small) {
        for (const it of items) it.text = (it.head && it.members.length > 1) ? suffixOf(it.members.length) : '';
    }

    //--> 4. Hover index per bar: every full text of a bar that has a hidden / altered text, plus a head's
    //*    hidden cluster members; precedence order, deduplicated.
    const byBar = new Map();
    const touch = (it, extra) => {
        const k = it.m.logical;
        if (k == null) return;
        if (!byBar.has(k)) byBar.set(k, { altered: false, list: [] });
        const e = byBar.get(k);
        if (it.full) e.list.push(it);
        if (extra) for (const x of extra) if (x !== it) e.list.push(x);
        if (it.full && it.text !== it.full) e.altered = true;
    };
    for (const it of items) touch(it, it.head ? it.members : null);
    const hoverByIndex = {};
    for (const [k, e] of byBar) {
        if (!e.altered) continue;
        const seen = new Set(); const texts = [];
        e.list.sort(byPriority);
        for (const it of e.list) { if (seen.has(it)) continue; seen.add(it); texts.push(it.full); }
        hoverByIndex[k] = texts;
    }

    return { markers: items.map(it => ({ ...it.m, text: it.text })), hoverByIndex };
};
